/**
 * OAuth Connection Service
 * Handles OAuth connections for email sending and calendar synchronization
 * Supports Google (Gmail + Google Calendar) and Microsoft (Outlook + Microsoft Calendar)
 */

import { getBackendUrl } from "./config";
import { endpoint } from "./endpoints";
import { getSupabaseClient } from "./supabaseClient";
import { isNativePlatform } from "@/lib/capacitor/platform";
import AgencyTokenManager from "./agencyTokenManager";
import type {
  EmailOAuthConnection,
  EmailOAuthConnectionResponse,
  EmailOAuthProvider,
} from "../types/email-oauth";
import type {
  CalendarConnectionStatus,
  CalendarSyncPreferences,
  SyncConflict,
  SyncConflictResolution,
  ManualSyncRequest,
  ManualSyncResponse,
} from "../../(authenticated)/appointments/_modules/types/calendar-sync";

class OAuthConnectionService {
  /**
   * Get Supabase access token for authentication
   */
  /**
   * Headers for the raw connect fetches — MUST mirror the axios client's
   * agency context. Without x-agency-id the guard resolves an arbitrary
   * membership: a user who is OWNER of the current agency but VIEWER in any
   * other gets the viewer write-block on POST (bare 403 "Forbidden").
   */
  private buildAuthHeaders(accessToken: string): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    };
    const agencyId = AgencyTokenManager.getCurrentAgencyId();
    if (agencyId) headers["x-agency-id"] = agencyId;
    return headers;
  }

  private async getAccessToken(): Promise<string | null> {
    try {
      const supabase = await getSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();
      return session?.access_token || null;
    } catch (error) {
      console.error("Failed to get access token:", error);
      return null;
    }
  }

  /**
   * Initiate OAuth connection for Google (Gmail + Google Calendar)
   * Sends a secure POST request to get the OAuth URL, then redirects
   */
  async connectGoogle(returnUrl?: string): Promise<void> {
    // Native app: consent must run in the SYSTEM browser sheet — Google
    // refuses OAuth inside an embedded webview (disallowed_useragent) — and
    // the callback redirect lands the SHEET (which has no app session) on the
    // public /oauth-complete page, not settings. The component polls status
    // and closes the sheet once the backend has the tokens.
    const native = isNativePlatform();
    const redirectUrl =
      returnUrl ||
      (native
        ? `${window.location.origin}/oauth-complete`
        : `${window.location.origin}/settings?tab=account`);

    // Get user's browser timezone for calendar sync
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Get access token for authentication
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      console.error("No access token available for OAuth connection");
      return;
    }

    try {
      // Send POST request with token in Authorization header (secure)
      // Include user's timezone so calendar events sync with correct times
      const response = await fetch(getBackendUrl(endpoint.oauth.connectGoogle), {
        method: "POST",
        headers: this.buildAuthHeaders(accessToken),
        body: JSON.stringify({ returnUrl: redirectUrl, timezone: userTimezone }),
        credentials: "include",
      });

      if (!response.ok) {
        // statusText alone ("Forbidden") hides WHICH gate rejected — surface
        // the backend's actual message (MFA_REQUIRED, demo, suspension, …).
        const body = await response.text().catch(() => "");
        throw new Error(
          `Failed to initiate Google OAuth: ${response.status} ${body || response.statusText}`,
        );
      }

      const result = await response.json();

      // Backend returns the OAuth URL to redirect to
      if (result.data?.authUrl) {
        if (native) {
          const { Browser } = await import("@capacitor/browser");
          await Browser.open({ url: result.data.authUrl });
        } else {
          window.location.href = result.data.authUrl;
        }
      } else {
        throw new Error("No auth URL returned from server");
      }
    } catch (error) {
      console.error("Error initiating Google OAuth:", error);
      // Native: never navigate the webview itself — surface to the caller's
      // toast instead.
      if (native) throw error;
      // Redirect to settings with error
      window.location.href = `${redirectUrl}?oauth=error`;
    }
  }

  /**
   * Initiate OAuth connection for Microsoft (Outlook + Microsoft Calendar)
   * Sends a secure POST request to get the OAuth URL, then redirects
   */
  async connectMicrosoft(returnUrl?: string): Promise<void> {
    // Same native-vs-web split as connectGoogle — see the comment there.
    const native = isNativePlatform();
    const redirectUrl =
      returnUrl ||
      (native
        ? `${window.location.origin}/oauth-complete`
        : `${window.location.origin}/settings?tab=account`);

    // Get user's browser timezone for calendar sync
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Get access token for authentication
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      console.error("No access token available for OAuth connection");
      return;
    }

    try {
      // Send POST request with token in Authorization header (secure)
      // Include user's timezone so calendar events sync with correct times
      const response = await fetch(getBackendUrl(endpoint.oauth.connectMicrosoft), {
        method: "POST",
        headers: this.buildAuthHeaders(accessToken),
        body: JSON.stringify({ returnUrl: redirectUrl, timezone: userTimezone }),
        credentials: "include",
      });

      if (!response.ok) {
        const body = await response.text().catch(() => "");
        throw new Error(
          `Failed to initiate Microsoft OAuth: ${response.status} ${body || response.statusText}`,
        );
      }

      const result = await response.json();

      // Backend returns the OAuth URL to redirect to
      if (result.data?.authUrl) {
        if (native) {
          const { Browser } = await import("@capacitor/browser");
          await Browser.open({ url: result.data.authUrl });
        } else {
          window.location.href = result.data.authUrl;
        }
      } else {
        throw new Error("No auth URL returned from server");
      }
    } catch (error) {
      console.error("Error initiating Microsoft OAuth:", error);
      if (native) throw error;
      // Redirect to settings with error
      window.location.href = `${redirectUrl}?oauth=error`;
    }
  }

  /**
   * Get connection status for both Google and Microsoft
   * Returns email and calendar connection information
   */
  async getConnectionStatus(): Promise<{
    google?: EmailOAuthConnection;
    microsoft?: EmailOAuthConnection;
    error?: string;
  }> {
    try {
      const accessToken = await this.getAccessToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      const response = await fetch(getBackendUrl(endpoint.oauth.getConnectionStatus), {
        method: "GET",
        headers,
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`Failed to get connection status: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data || result;
    } catch (error) {

      return {
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Disconnect OAuth provider (removes email and calendar access)
   */
  async disconnectProvider(provider: EmailOAuthProvider): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const accessToken = await this.getAccessToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      const response = await fetch(
        getBackendUrl(endpoint.oauth.disconnectProvider(provider)),
        {
          method: "DELETE",
          headers,
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to disconnect ${provider}: ${response.statusText}`);
      }

      return { success: true };
    } catch (error) {

      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Get calendar sync preferences
   */
  async getCalendarPreferences(): Promise<CalendarSyncPreferences | null> {
    try {
      const accessToken = await this.getAccessToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      const response = await fetch(
        getBackendUrl(endpoint.oauth.getCalendarPreferences),
        {
          method: "GET",
          headers,
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get calendar preferences");
      }

      const result = await response.json();
      // Extract data from response wrapper {statusCode, data, message}
      return result.data || result;
    } catch (error) {

      return null;
    }
  }

  /**
   * Update calendar sync preferences
   */
  async updateCalendarPreferences(
    preferences: Partial<CalendarSyncPreferences>
  ): Promise<{
    success: boolean;
    preferences?: CalendarSyncPreferences;
    error?: string;
  }> {
    try {
      const accessToken = await this.getAccessToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      const response = await fetch(
        getBackendUrl(endpoint.oauth.updateCalendarPreferences),
        {
          method: "PATCH",
          headers,
          body: JSON.stringify(preferences),
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update calendar preferences");
      }

      const data = await response.json();
      return {
        success: true,
        preferences: data,
      };
    } catch (error) {

      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Get calendar connections status (detailed info about calendars)
   */
  async getCalendarConnections(): Promise<CalendarConnectionStatus | null> {
    try {
      const accessToken = await this.getAccessToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      const response = await fetch(
        getBackendUrl(endpoint.oauth.getCalendarConnections),
        {
          method: "GET",
          headers,
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get calendar connections");
      }

      return await response.json();
    } catch (error) {

      return null;
    }
  }

  /**
   * Manually sync a specific appointment to connected calendars
   */
  async syncAppointment(
    appointmentId: string,
    provider?: "google" | "microsoft"
  ): Promise<ManualSyncResponse> {
    try {
      const accessToken = await this.getAccessToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      const url = provider
        ? `${endpoint.oauth.syncAppointment(appointmentId)}?provider=${provider}`
        : endpoint.oauth.syncAppointment(appointmentId);

      const response = await fetch(getBackendUrl(url), {
        method: "POST",
        headers,
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to sync appointment");
      }

      return await response.json();
    } catch (error) {

      return {
        success: false,
        googleSync: { success: false, error: "Failed to sync" },
        microsoftSync: { success: false, error: "Failed to sync" },
      };
    }
  }

  /**
   * Sync all appointments to connected calendars
   */
  async syncAllAppointments(): Promise<{
    success: boolean;
    synced: number;
    failed: number;
    error?: string;
  }> {
    try {
      const accessToken = await this.getAccessToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      const response = await fetch(
        getBackendUrl(endpoint.oauth.syncAllAppointments),
        {
          method: "POST",
          headers,
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to sync all appointments");
      }

      const result = await response.json();

      // Backend returns { statusCode, data: { success, eventsProcessed, appointmentsCreated, ... }, message }
      // Map to the expected format
      const data = result.data || result;
      return {
        success: data.success && (!data.errors || data.errors.length === 0),
        synced: (data.appointmentsCreated || 0) + (data.appointmentsUpdated || 0),
        failed: data.errors?.length || 0,
        error: data.errors?.join(", ") || undefined,
      };
    } catch (error) {

      return {
        success: false,
        synced: 0,
        failed: 0,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Get all unresolved sync conflicts
   */
  async getSyncConflicts(): Promise<SyncConflict[]> {
    try {
      const accessToken = await this.getAccessToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      const response = await fetch(
        getBackendUrl(endpoint.oauth.getSyncConflicts),
        {
          method: "GET",
          headers,
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get sync conflicts");
      }

      return await response.json();
    } catch (error) {

      return [];
    }
  }

  /**
   * Resolve a sync conflict
   */
  async resolveSyncConflict(
    conflictId: string,
    resolution: SyncConflictResolution
  ): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const accessToken = await this.getAccessToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      const response = await fetch(
        getBackendUrl(endpoint.oauth.resolveSyncConflict(conflictId)),
        {
          method: "POST",
          headers,
          body: JSON.stringify(resolution),
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to resolve sync conflict");
      }

      return { success: true };
    } catch (error) {

      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Check if user has any OAuth provider connected
   */
  async hasOAuthConnected(): Promise<boolean> {
    const status = await this.getConnectionStatus();
    return !!(status.google || status.microsoft);
  }

  /**
   * Check if user has calendar sync enabled
   */
  async hasCalendarSyncEnabled(): Promise<boolean> {
    const preferences = await this.getCalendarPreferences();
    return !!(
      preferences &&
      (preferences.googleEnabled || preferences.microsoftEnabled)
    );
  }

  /**
   * Check token health for a provider
   */
  async checkTokenHealth(provider: "google" | "microsoft"): Promise<{
    status: "healthy" | "expired" | "error";
    message?: string;
  }> {
    try {
      const accessToken = await this.getAccessToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      const response = await fetch(
        getBackendUrl(`${endpoint.oauth.base}/${provider}/health`),
        {
          method: "GET",
          headers,
          credentials: "include",
        }
      );

      if (!response.ok) {
        return { status: "error", message: "Failed to check token health" };
      }

      const result = await response.json();
      return result.data || { status: "error" };
    } catch (error) {
      return {
        status: "error",
        message: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Get list of calendars from provider
   */
  async getCalendarList(provider: "google" | "microsoft"): Promise<{
    calendars: Array<{
      id: string;
      name: string;
      primary: boolean;
    }>;
    error?: string;
  }> {
    try {
      const accessToken = await this.getAccessToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      const response = await fetch(
        getBackendUrl(`${endpoint.oauth.base}/${provider}/calendars`),
        {
          method: "GET",
          headers,
          credentials: "include",
        }
      );

      if (!response.ok) {
        return { calendars: [], error: "Failed to fetch calendar list" };
      }

      const result = await response.json();
      return result.data || { calendars: [] };
    } catch (error) {
      return {
        calendars: [],
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Select a calendar for sync
   */
  async selectCalendar(
    provider: "google" | "microsoft",
    calendarId: string,
    calendarName?: string
  ): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const accessToken = await this.getAccessToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      const response = await fetch(
        getBackendUrl(`${endpoint.oauth.base}/${provider}/calendars/select`),
        {
          method: "POST",
          headers,
          body: JSON.stringify({ calendarId, calendarName }),
          credentials: "include",
        }
      );

      if (!response.ok) {
        return { success: false, error: "Failed to select calendar" };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }
}

// Export singleton instance
export const oauthConnectionService = new OAuthConnectionService();
